import { CSSProperties } from "react";
import { PartialBrandTheme, PartialQuestThemeData, StyleConfig } from "./leaderBoardTypes";
import { hexToGlassColor } from "../../../../general";

class LeaderboardThemeManager {
  private themeConfig: any;
  private styleConfig: StyleConfig;
  private questThemeData: PartialQuestThemeData;
  private brandTheme: PartialBrandTheme;

  constructor(
    themeConfig = {},
    styleConfig: StyleConfig = {},
    questThemeData: PartialQuestThemeData = {
      accentColor: "",
      theme: "",
      borderRadius: "",
      buttonColor: "",
      images: [],
    },
    brandTheme: PartialBrandTheme = {
      accentColor: "",
      background: "",
      borderRadius: "",
      primaryButton: "",
      contentColor: "",
      fontFamily: "",
      logo: "",
      primaryColor: "",
      secondaryColor: "",
      tertiaryColor: "",
      titleColor: "",
    }
  ) {
    this.themeConfig = themeConfig;
    this.styleConfig = styleConfig;
    this.questThemeData = questThemeData;
    this.brandTheme = brandTheme;
  }

  getContainerStyles(): CSSProperties {
    return {
      background: this.styleConfig?.Form?.backgroundColor || !(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 'linear-gradient(13deg, #fff 38.24%, #9035ff 59.39%, #0065ff 70.7%)' : (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) : `linear-gradient(14deg, rgba(255, 255, 255, 0) 36.47%, ${hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.6)} 60.34%, ${this.brandTheme?.primaryColor || this.questThemeData?.accentColor} 73.12%)` || this.questThemeData?.accentColor,
      color: this.styleConfig?.Form?.color || this.themeConfig?.primaryColor || this.brandTheme?.titleColor,
      fontFamily: this.styleConfig?.Form?.fontFamily ||
        this.brandTheme?.borderRadius ||
        this.themeConfig?.fontFamily,
      borderRadius: this.styleConfig?.Form?.borderRadius ||
        `${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} 12px 12px`,
      ...this.styleConfig?.Form,
    };
  }

  // Main heading styles
  getMainHeadingStyles(): CSSProperties {
    return {
      color: this.styleConfig?.MainHeading?.color ||
        "white",
      ...this.styleConfig?.MainHeading,
    };
  }

  // Section heading styles
  getHeadingStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Heading?.color ||
        this.brandTheme?.titleColor ||
        this.themeConfig?.primaryColor,
      ...this.styleConfig?.Heading,
    };
  }

  // Description styles
  getDescriptionStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Description?.color || 
             this.brandTheme?.contentColor || 
             this.themeConfig?.secondaryColor,
      ...this.styleConfig?.Description,
    };
  }

  // Top rank styles
  getTopRankStyles() {
    return {
      name: {
        color: this.styleConfig?.MainHeading?.color || "white",
      },
      points: {
        ...this.styleConfig?.PointsBackground,
        color: this.styleConfig?.PointsColor?.color || this.brandTheme?.titleColor || this.questThemeData?.accentColor,
      },
      indexNumber: {
        ...this.styleConfig?.IndexColor,
        ...this.styleConfig?.IndexBackground,
      },
    };
  }

  // Leaderboard card styles
  getLeaderboardCardStyles(isPresentUser: boolean) {
    return {
      container: {
        ...(isPresentUser ? this.styleConfig?.PresentUser : {}),
      },
      index: {
        ...this.styleConfig?.IndexColor,
        ...this.styleConfig?.IndexBackground,
        color: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 1) : "#9035ff",
        background: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.1) : "#f4ebff"
      },
      name: {
        color: this.styleConfig?.Heading?.color || 
               this.brandTheme?.titleColor || 
               this.themeConfig?.primaryColor,
      },
      points: {
        ...this.styleConfig?.PointsColor,
        color: this.brandTheme?.contentColor || this.questThemeData?.buttonColor || this.brandTheme?.primaryColor || this.questThemeData?.accentColor,
      },
      progressBar: {
        ...this.styleConfig?.ProgressBarColor,
        background: this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035ff",
      },
      icon: {
        color: this.styleConfig?.IconStyle?.color || this.brandTheme?.titleColor || this.questThemeData?.buttonColor || this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035FF",
      },
    };
  }

  // Inner section background
  getInnerSectionStyles(): CSSProperties {
    return {
      ...this.styleConfig?.InnerBackground,
      borderRadius: this.styleConfig?.Form?.borderRadius || this.questThemeData?.borderRadius || this.brandTheme?.borderRadius,
    };
  }

  // Footer styles
  getFooterStyles() {
    return {
      container: {
        background: this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
                   this.styleConfig?.Form?.backgroundColor ||
                   this.styleConfig?.Form?.background ||
                   this.themeConfig?.backgroundColor,
        borderRadius: this.styleConfig?.Form?.borderRadius || `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
        ...this.styleConfig?.Footer?.FooterStyle,
      },
      text: this.styleConfig?.Footer?.FooterTextStyle || {},
      icon: this.styleConfig?.Footer?.FooterIcon || {},
      content: {
        text: this.styleConfig?.Footer?.FooterText,
        link: this.styleConfig?.Footer?.FooterLink,
      },
    };
  }

  // Update methods
  updateThemeConfig(newConfig: any) {
    this.themeConfig = { ...this.themeConfig, ...newConfig };
  }

  updateStyleConfig(newConfig: Partial<StyleConfig>) {
    this.styleConfig = { ...this.styleConfig, ...newConfig };
  }

  updateQuestTheme(newTheme: PartialQuestThemeData) {
    this.questThemeData = { ...this.questThemeData, ...newTheme };
  }

  updateBrandTheme(newTheme: PartialBrandTheme) {
    this.brandTheme = { ...this.brandTheme, ...newTheme };
  }

  getTopLeaderboardContainerStyles(): CSSProperties {
    return {
      borderRadius: this.styleConfig?.Form?.borderRadius || `${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}`,
    }
  }
}

function toGlassColor(color, opacity = 0.05) {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  // Create a dummy element to let the browser parse the color
  const temp = document.createElement("div");
  temp.style.color = color;
  document.body.appendChild(temp);

  const computedColor = getComputedStyle(temp).color;
  document.body.removeChild(temp);

  // Extract RGB values
  const match = computedColor.match(/rgba?\((\d+), (\d+), (\d+)(?:, ([\d.]+))?\)/);
  if (!match) return `rgba(0, 0, 0, ${opacity})`;

  const [, r, g, b] = match.map(Number);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export default LeaderboardThemeManager;
